import firebase from "firebase/compat/app";
import { useEffect, useRef, useState } from "react";
import {
  Box,
  List,
  Stack,
  Avatar,
  Divider,
  ListItem,
  TextField,
  IconButton,
  Typography,
  FormControl,
  ListItemText,
  ListItemAvatar,
  LinearProgress,
  CircularProgress
} from "@material-ui/core";
import useAuth from "hooks/useAuth";
import useSettings from "hooks/useSettings";
import useFirestore from "hooks/useFirestore";
import { fDateTimeSuffix } from "utils/formatTime";
import { Delete, Send } from "@material-ui/icons";
import { useTranslation } from "react-i18next";
import {
  RealEstateContact,
  SharedContact,
  SharedContactComment
} from "../../../../../@types/sharedSchema";
import { db } from "firebaseConfig";
import { getRandomId } from "utils/Haim/utils";
import { convertFirestoreDatesToJsDates, GenericConverter } from "utils/firestoreConverters";
import type { ChangeEvent, FormEvent } from "react";

type ContactTableNestedRowSharedCommentsProps = {
  contact: RealEstateContact;
};

export function ContactTableNestedRowSharedComments({
  contact
}: ContactTableNestedRowSharedCommentsProps): JSX.Element {
  const [sharedContact, setSharedContact] = useState<null | SharedContact>(null);

  const [initialLoading, setInitialLoading] = useState(true);
  const [newCommentLoading, setNewCommentLoading] = useState(false);

  const [newComment, setNewComment] = useState("");

  const {
    user: { email }
  } = useAuth();

  const { themeDirection } = useSettings();

  const { userSettings, organization, organizationId } = useFirestore();

  const { photoURL, ownerFullName } = userSettings ?? {};

  const { t: translate } = useTranslation();

  const formRef = useRef<HTMLFormElement>(null);

  // The document id IS the contact's phone (see `contactRekey.ts`). `callerPhoneNumber`
  // is a legacy mirror: absent on ~90% of documents and, where present, sometimes a
  // local-format string that agrees with nothing.
  const contactPhone = contact.id;

  useEffect(() => {
    const sharedContactsListener = db
      .collection("dashboard-orgs")
      .doc(organizationId)
      .collection("shared-contacts")
      .doc(contactPhone)
      .withConverter(GenericConverter<SharedContact>())
      .onSnapshot(
        (snapshot) => {
          let data = snapshot.data() ?? null;

          if (data) {
            data.comments = data.comments.map(
              (comment) => convertFirestoreDatesToJsDates(comment) as SharedContactComment
            );
          }

          setSharedContact(data);
          setInitialLoading(false);
        },
        () => {
          setInitialLoading(false);
        }
      );

    return sharedContactsListener;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactPhone, organizationId]);

  const sharedComments = sharedContact?.comments ?? [];

  const handleCommentChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setNewComment(value);
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();

    setNewCommentLoading(true);

    type ParsedSharedContact = Omit<
      SharedContact,
      "id" | "createdTime" | "updatedTime" | "comments"
    > & {
      comments: firebase.firestore.FieldValue;
    };

    const newCommentForm: SharedContactComment = {
      id: getRandomId(),
      userId: email,
      comment: newComment,
      commentTime: new Date(),
      userPhotoURL: photoURL,
      userDisplayName: ownerFullName
    };

    const newSharedContact: ParsedSharedContact = {
      comments: firebase.firestore.FieldValue.arrayUnion(newCommentForm),
      organizationId,
      callerPhoneNumberInternational: contactPhone
    };

    try {
      await db
        .collection("dashboard-orgs")
        .doc(organizationId)
        .collection("shared-contacts")
        .doc(contactPhone)
        .set(newSharedContact, { merge: true });

      setNewComment("");
    } catch (error) {
      console.error("Failed to add new comment:", error);
    } finally {
      setNewCommentLoading(false);
    }
  };

  const handleRemoveComment = (commentId: string) => async (): Promise<void> => {
    if (!sharedContact) return;

    setNewCommentLoading(true);

    const commentToDelete = sharedContact.comments.find((comment) => comment.id === commentId);

    if (!commentToDelete) {
      setNewCommentLoading(false);
      return;
    }

    try {
      await db
        .collection("dashboard-orgs")
        .doc(organizationId)
        .collection("shared-contacts")
        .doc(contactPhone)
        .set(
          {
            comments: firebase.firestore.FieldValue.arrayRemove(commentToDelete)
          },
          { merge: true }
        );
    } catch (error) {
      console.error("Failed to remove comment:", error);
    } finally {
      setNewCommentLoading(false);
    }
  };

  const isRTL = themeDirection === "rtl";

  const loading = initialLoading || newCommentLoading;

  const isAdmin = organization?.admins?.includes(email) ?? false;

  return (
    <>
      <Divider />
      <Stack gap={2}>
        <Typography variant="h6">{translate("contactsPage.sharedComments.title")}</Typography>
        <List
          sx={{
            py: 0,
            display: "flex",
            maxHeight: 256,
            overflowY: "auto",
            flexDirection: "column-reverse"
          }}
        >
          <Box>
            {initialLoading ? (
              <LinearProgress />
            ) : sharedComments.length === 0 ? (
              <Typography variant="body1">
                {translate("contactsPage.error.noCommentsFound")}
              </Typography>
            ) : (
              sharedComments.map(
                ({ id, comment, commentTime, userId, userDisplayName, userPhotoURL }) => {
                  const isOwner = userId === email;
                  const isAuthorized = isOwner || isAdmin;
                  return (
                    <ListItem
                      secondaryAction={
                        isAuthorized && (
                          <IconButton
                            edge="end"
                            color="error"
                            onClick={handleRemoveComment(id)}
                            disabled={loading}
                          >
                            <Delete />
                          </IconButton>
                        )
                      }
                      key={id}
                    >
                      <ListItemAvatar>
                        <Avatar src={userPhotoURL} alt={userDisplayName} />
                      </ListItemAvatar>
                      <ListItemText
                        primary={
                          <Stack direction="row" gap={1} alignItems="center" flexWrap="wrap">
                            <Typography variant="body1" sx={{ fontWeight: "bold" }}>
                              {userDisplayName}
                            </Typography>
                            <Typography variant="body2" color="textSecondary">
                              {fDateTimeSuffix(commentTime)}
                            </Typography>
                          </Stack>
                        }
                        secondary={<Typography variant="body2">{comment}</Typography>}
                      />
                    </ListItem>
                  );
                }
              )
            )}
          </Box>
        </List>
        <FormControl fullWidth ref={formRef} component="form" onSubmit={handleSubmit}>
          <Stack direction="row" gap={1} alignItems="center">
            <TextField
              required
              fullWidth
              variant="outlined"
              label={translate("contactsPage.sharedComments.newComment")}
              value={newComment}
              disabled={loading}
              InputProps={{
                endAdornment: loading ? <CircularProgress size={20} /> : null
              }}
              onChange={handleCommentChange}
            />
            <IconButton type="submit" size="large" disabled={loading}>
              <Send
                sx={{
                  transform: isRTL ? "rotate(180deg)" : "rotate(0deg)",
                  transition: "transform 200ms"
                }}
              />
            </IconButton>
          </Stack>
        </FormControl>
      </Stack>
    </>
  );
}
